import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { ENABLE_PERMISSION_CHECK } from '../config/featureFlags';
import { ensureRbacSnapshot } from '../rbac/permissionService';
import { loadRbacSnapshot } from '../rbac/permissionStorage';
import type { AccessRequirement, PermissionCode, RoleCode, RbacSnapshot } from '../rbac/types';
import { canAccess } from '../rbac/permissionUtils';

type AccessControlState =
  | { status: 'idle' | 'loading' }
  | { status: 'ready'; snapshot: RbacSnapshot; permissionSet: ReadonlySet<PermissionCode> }
  | { status: 'error'; message: string };

type AccessControlValue = {
  status: AccessControlState['status'];
  loading: boolean;
  snapshot: RbacSnapshot | null;
  roleCode: RoleCode;
  permissions: ReadonlySet<PermissionCode>;
  refresh: () => Promise<void>;
  canAccess: (req?: AccessRequirement) => boolean;
};

const Ctx = createContext<AccessControlValue | undefined>(undefined);

export function AccessControlProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AccessControlState>(() => {
    const cached = loadRbacSnapshot();
    if (cached && cached.permissions?.length) {
      const set = new Set(cached.permissions);
      return { status: 'ready', snapshot: cached, permissionSet: set };
    }
    return { status: 'idle' };
  });

  const refreshRef = useRef<() => Promise<void>>(async () => {});

  const refresh = useCallback(async () => {
    if (!ENABLE_PERMISSION_CHECK) {
      // In bypass mode, don't block UI on any permission fetch.
      const cached = loadRbacSnapshot();
      const snap: RbacSnapshot =
        cached ??
        ({
          roleId: 0,
          roleName: 'Developer',
          roleCode: 'UNKNOWN',
          permissions: [],
          fetchedAt: Date.now(),
        } satisfies RbacSnapshot);
      setState({ status: 'ready', snapshot: snap, permissionSet: new Set(snap.permissions) });
      return;
    }

    setState({ status: 'loading' });
    const res = await ensureRbacSnapshot();
    if (!res.ok) {
      // If active-user hasn't been synced yet (post-login race), retry shortly.
      if (res.unauthenticated) {
        setState({ status: 'loading' });
        window.setTimeout(() => {
          void refreshRef.current();
        }, 250);
        return;
      }
      setState({ status: 'error', message: res.error });
      return;
    }
    setState({ status: 'ready', snapshot: res.snapshot, permissionSet: new Set(res.snapshot.permissions) });
  }, []);

  useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);

  useEffect(() => {
    if (!ENABLE_PERMISSION_CHECK) {
      void refresh();
      return;
    }
    if (state.status === 'idle') void refresh();
  }, [refresh, state.status]);

  const value = useMemo<AccessControlValue>(() => {
    if (state.status === 'ready') {
      return {
        status: state.status,
        loading: false,
        snapshot: state.snapshot,
        roleCode: state.snapshot.roleCode,
        permissions: state.permissionSet,
        refresh,
        canAccess: (req?: AccessRequirement) => canAccess(req, { roleCode: state.snapshot.roleCode, permissions: state.permissionSet }),
      };
    }
    if (state.status === 'error') {
      return {
        status: state.status,
        loading: false,
        snapshot: null,
        roleCode: 'UNKNOWN',
        permissions: new Set(),
        refresh,
        canAccess: () => !ENABLE_PERMISSION_CHECK,
      };
    }
    return {
      status: state.status,
      loading: true,
      snapshot: null,
      roleCode: 'UNKNOWN',
      permissions: new Set(),
      refresh,
      canAccess: () => !ENABLE_PERMISSION_CHECK,
    };
  }, [refresh, state]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAccessControl(): AccessControlValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAccessControl must be used within AccessControlProvider');
  return v;
}

